/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Apr 11 2024
 **/

import { useEffect, useState } from "react";

import { Button, Row, Typography } from "@/frameworks/designStuff/components";
import { Choice } from "@/frameworks/libraries/components";
import { _T } from "@rfice/language";

import { useTusUploadTargetRenderer, useUploadTus } from "@/frameworks/core/hooks";

export default function UploadingStatus() {
	const tusUploadState = useTusUploadTargetRenderer("FILEBOX");
	const [uploadingStatus, setUploadingStatus] = useState<"PENDING" | "SUCCESS" | "FAILED">("PENDING");

	const uploadingFileCount = Object.keys(tusUploadState).length;
	const isUploadFinished = Object.values(tusUploadState).every((state) => state.status !== "pending");
	const isUploadFailed = Object.values(tusUploadState).every((state) => state.status === "error");

	// filter out error state
	const filteredObject = Object.fromEntries(
		Object.entries(tusUploadState).filter((entry) => entry[1].status !== "error")
	);
	const filteredFileCount = Object.keys(filteredObject).length;

	const { cancelUpload } = useUploadTus();

	const cancelAll = () => {
		Object.keys(tusUploadState).forEach((tusID) => {
			cancelUpload(tusID);
		});
	};

	useEffect(() => {
		if (isUploadFinished) {
			setUploadingStatus("SUCCESS");
			return;
		}

		if (isUploadFailed) {
			setUploadingStatus("FAILED");
			return;
		}
	}, [isUploadFailed, isUploadFailed, uploadingFileCount]);

	return (
		<Row verticalAlign="center" horizonAlign="distribute" css={{ width: "100%" }}>
			<Choice value={uploadingStatus}>
				<Choice.Case test="PENDING">
					<Typography typoSize="Title1_B">
						{_T("{{count}}개 파일 업로드 중", { count: filteredFileCount })}
					</Typography>
				</Choice.Case>
				<Choice.Case test={"SUCCESS"}>
					<Typography typoSize="Title1_B">
						{_T("{{count}}개 파일 업로드 완료", { count: filteredFileCount })}
					</Typography>
				</Choice.Case>
				<Choice.Case test={"FAILED"}>
					<Typography typoSize="Title1_B" typoColor="error_60_100">
						{_T("{{count}}개 파일 업로드 실패", { count: uploadingFileCount })}
					</Typography>
				</Choice.Case>
			</Choice>
			<Button filled="transparent" typoSize="Button2_underline" onClick={cancelAll}>
				{_T("전체 취소")}
			</Button>
		</Row>
	);
}
